/** A global, headless player */
import { logger, shortUuid } from '@tunasong/models'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { IAudioContext, IMediaElementAudioSourceNode } from 'standardized-audio-context'
import { useMixer } from '../hooks/mixer.hook.js'
import { useGlobalPlayerState } from './global-player-state.hook.js'

export const GlobalAudioPlayer = () => {
  const { setPlayerRef, currentSrc } = useGlobalPlayerState()

  const id = useMemo(() => shortUuid(), [])
  const mixer = useMixer()

  /** Register channel in the mixer */
  const [localRef, setLocalRef] = useState<HTMLAudioElement | null>(null)
  const elSource = useRef<IMediaElementAudioSourceNode<IAudioContext> | null>(null)

  const channelId = useRef<string>(null)

  const removeChannel = useCallback(() => {
    if (!channelId.current) {
      return
    }

    /**
     * We disconnect the MediaElementSource from downstream nodes but keep the
     * reference around. Per the Web Audio spec an HTMLMediaElement may be bound
     * to a MediaElementSourceNode only ONCE for its lifetime — re-creating one
     * after a teardown throws InvalidStateError ("HTMLMediaElement already
     * connected previously to a different MediaElementSourceNode"). Reusing the
     * existing source on the next setup is the only safe path. See GH #1181.
     */
    if (elSource.current) {
      try {
        elSource.current.disconnect()
      } catch (e) {
        // Handle possible errors if already disconnected
        logger.debug('Error disconnecting source', e)
      }
    }

    mixer.removeChannel(channelId.current)
    channelId.current = null
  }, [mixer])

  const setupChannel = useCallback(() => {
    if (!(localRef && mixer)) {
      return
    }

    const id = `audioplayer-${shortUuid()}`

    logger.debug('Setting up channel', {
      id,
      channelId: channelId.current,
      elSource,
      localRef,
    })
    // Remove previous channel
    removeChannel()

    channelId.current = id

    /**
     * If we already created a MediaElementSource for this <audio>, reuse it.
     * The Web Audio API forbids creating a second MediaElementSource for the
     * same HTMLMediaElement (throws InvalidStateError). On hot reload the
     * AudioEngine — and therefore the mixer's context — can change while the
     * <audio> DOM node survives, so the old source belongs to a disposed
     * context and binding a fresh one against the new context will fail. Catch
     * that case so HMR doesn't crash the React tree under the error boundary
     * (GH #1181). A full reload restores audio.
     */
    let source = elSource.current
    if (!source) {
      try {
        source = mixer.context.createMediaElementSource(localRef)
        elSource.current = source
      } catch (e) {
        logger.warn(
          'GlobalAudioPlayer: cannot create MediaElementSource — element is already bound (likely after hot reload). Reload the page to restore audio.',
          e
        )
        return
      }
    }

    mixer.addChannel(
      {
        id,
        name: 'Global Audio Player',
        type: 'general',
        // gain: volume,
        inputNode: source,
      },
      'media'
    )
    logger.debug('Channel is set up', {
      id,
      channelId: channelId.current,
      elSource,
      localRef,
    })
    // Update the global player reference
    setPlayerRef(localRef)
  }, [localRef, mixer, removeChannel, setPlayerRef])

  // Setup channel
  useEffect(() => {
    setupChannel()
    return () => {
      removeChannel()
    }
  }, [removeChannel, setupChannel])

  return <audio key={id} id={id} crossOrigin="anonymous" ref={setLocalRef} src={currentSrc} controls={false} />
}
